import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  adminDownloadUrl,
  adminUploadUrl,
  getMe,
  listAdminFiles,
  listMyFiles,
  myDownloadUrl,
  myUploadUrl,
  uploadToPresignedUrl,
  type Folder,
  type Listing,
  type MyProfile,
} from '../api';
import { useCan } from '../staff';

type Audience = 'admin' | 'partner';

// The file browser, shared by both audiences.
//
// The difference between them is only which endpoints it calls and what a path
// means: an admin's path is a bucket key prefix, a partner's is relative to their
// home directory and never leaves it. Neither version decides what is allowed --
// the API does that, using the same folder permissions as the SFTP session policy
// -- so this component's job is to not offer actions that will obviously be
// refused, and to show the refusal clearly when it happens anyway.
export function FilesPage({ audience }: { audience: Audience }) {
  const params = useParams();
  const path = params['*'] ?? '';

  const [listing, setListing] = useState<Listing>();
  const [profile, setProfile] = useState<MyProfile>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState<Record<string, string>>({});
  const fileInput = useRef<HTMLInputElement>(null);

  // Only the partner view needs the profile, for the folder permissions that
  // decide whether uploading here is even possible.
  useEffect(() => {
    if (audience !== 'partner') return;
    getMe()
      .then(setProfile)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, [audience]);

  const folder = audience === 'partner' ? matchFolder(profile?.folders, path) : undefined;
  // For staff, the two limits come from their role; for a partner, from the folder
  // permission, which is the same rule the SFTP session policy applies.
  const staffMayRead = useCan('files:read');
  const staffMayWrite = useCan('files:write');
  // Write-only means uploads only. The filenames are still listed, because the
  // SFTP session policy lists them too -- hiding them here would be a different
  // rule from the one that is actually enforced.
  const canRead =
    audience === 'admin'
      ? staffMayRead
      : folder === undefined || folder.permission !== 'write-only';

  const load = useCallback(async () => {
    // The partner view waits for the profile, because the profile decides what
    // this page may offer. Rendering a Download button and then having the API
    // refuse it is a worse answer than waiting one request.
    if (audience === 'partner' && !profile) return;
    setBusy(true);
    setError(undefined);
    try {
      setListing(audience === 'admin' ? await listAdminFiles(path) : await listMyFiles(path));
    } catch (err) {
      setListing(undefined);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [audience, path, profile]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = async () => {
    if (!listing?.continuationToken) return;
    setBusy(true);
    try {
      const next =
        audience === 'admin'
          ? await listAdminFiles(path, listing.continuationToken)
          : await listMyFiles(path, listing.continuationToken);
      setListing({
        folders: [...listing.folders, ...next.folders],
        files: [...listing.files, ...next.files],
        continuationToken: next.continuationToken,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const download = async (key: string, name: string) => {
    setError(undefined);
    try {
      const { url } =
        audience === 'admin' ? await adminDownloadUrl(key) : await myDownloadUrl(key);
      // The signed URL carries Content-Disposition: attachment, so this starts a
      // download instead of navigating -- which matters because a presigned URL
      // is a bearer credential and should not end up in the address bar or in
      // this tab's history.
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = name;
      anchor.rel = 'noreferrer';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const upload = async (files: FileList) => {
    setError(undefined);
    for (const file of Array.from(files)) {
      const target = path === '' ? file.name : `${path}/${file.name}`;
      setUploads((current) => ({ ...current, [file.name]: 'uploading…' }));
      try {
        // The same content type is used to sign the URL and to send the body. S3
        // checks the signature against the header, so a mismatch is a 403 that
        // reads like a permissions problem and is not one.
        const contentType = file.type || 'application/octet-stream';
        const { url } =
          audience === 'admin'
            ? await adminUploadUrl(target, contentType)
            : await myUploadUrl(target, contentType);
        await uploadToPresignedUrl(url, file);
        setUploads((current) => ({ ...current, [file.name]: 'done' }));
      } catch (err) {
        setUploads((current) => ({
          ...current,
          [file.name]: err instanceof Error ? err.message : String(err),
        }));
      }
    }
    await load();
  };

  const canUpload =
    audience === 'admin'
      ? staffMayWrite && path !== ''
      : folder !== undefined && (folder.permission ?? 'read-write') !== 'read-only';

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>{audience === 'admin' ? 'Files' : 'My files'}</h1>
          <Breadcrumb path={path} root={audience === 'admin' ? 'bucket' : 'home'} />
        </div>
        <div className="actions">
          <button type="button" className="link" onClick={() => void load()} disabled={busy}>
            Refresh
          </button>
          {canUpload && (
            <>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(event) => {
                  if (event.target.files?.length) void upload(event.target.files);
                  event.target.value = '';
                }}
              />
              <button type="button" onClick={() => fileInput.current?.click()}>
                Upload here
              </button>
            </>
          )}
        </div>
      </header>

      {!canRead &&
        (audience === 'admin' ? (
          <p className="hint card">
            Your role can see which files exist and when they arrived, which is what a “did it
            arrive?” question needs, but not open them. The contents belong to the partner.
          </p>
        ) : (
          <p className="hint card">
            This is a drop box: you can upload here and see what you have uploaded, but not download
            it again. That is the same access you have over SFTP.
          </p>
        ))}
      {audience === 'admin' && path === '' && (
        <p className="hint">
          The top level of the bucket: one folder per user, named after their username.
        </p>
      )}
      {audience === 'partner' && path === '' && (
        <p className="hint">Your folders. This is the same view you get when you connect over SFTP.</p>
      )}

      {Object.keys(uploads).length > 0 && (
        <ul className="uploads">
          {Object.entries(uploads).map(([name, state]) => (
            <li key={name} className={state === 'done' || state === 'uploading…' ? undefined : 'error'}>
              {name} — {state}
            </li>
          ))}
        </ul>
      )}

      {error && <p className="error">{error}</p>}

      {!listing ? (
        busy ? (
          <p>Loading…</p>
        ) : null
      ) : listing.folders.length === 0 && listing.files.length === 0 ? (
        <p className="hint">This folder is empty.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Size</th>
              <th>Modified</th>
              <th>Storage</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {path !== '' && (
              <tr>
                <td colSpan={5}>
                  <Link to={`/files/${encodePath(parentOf(path))}`}>← up one level</Link>
                </td>
              </tr>
            )}
            {listing.folders.map((entry) => (
              <tr key={entry.path}>
                <td>
                  <Link to={`/files/${encodePath(trimSlash(entry.path))}`}>{entry.name}/</Link>
                </td>
                <td colSpan={4} />
              </tr>
            ))}
            {listing.files.map((file) => (
              <tr key={file.key}>
                <td>{file.name}</td>
                <td>{formatSize(file.size)}</td>
                <td>{file.lastModified ? new Date(file.lastModified).toLocaleString() : ''}</td>
                <td className="hint">{storageLabel(file.storageClass)}</td>
                <td>
                  {canRead ? (
                    <button
                      type="button"
                      className="link"
                      onClick={() => void download(file.key, file.name)}
                    >
                      Download
                    </button>
                  ) : (
                    <span className="hint">upload only</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {listing?.continuationToken && (
        <button type="button" className="link" onClick={() => void loadMore()} disabled={busy}>
          {busy ? 'Loading…' : 'Load more'}
        </button>
      )}
      {audience === 'admin' && path === '' && (
        <p className="hint">
          Uploading needs a folder: pick a user's directory first, so a stray file cannot land at the
          root of the bucket where nothing can reach it.
        </p>
      )}
      {audience === 'partner' && path !== '' && folder?.permission === 'read-only' && (
        <p className="hint">This folder is read-only, so there is nothing to upload here.</p>
      )}
    </section>
  );
}

function Breadcrumb({ path, root }: { path: string; root: string }) {
  const segments = path === '' ? [] : path.split('/');
  return (
    <p className="breadcrumb">
      <Link to="/files">{root}</Link>
      {segments.map((segment, index) => (
        <span key={index}>
          {' / '}
          {index === segments.length - 1 ? (
            <span>{segment}</span>
          ) : (
            <Link to={`/files/${encodePath(segments.slice(0, index + 1).join('/'))}`}>{segment}</Link>
          )}
        </span>
      ))}
    </p>
  );
}

// Longest match, the same rule the API applies, so the UI's idea of which
// permission is in force cannot differ from the one that gets enforced.
const matchFolder = (folders: Folder[] | undefined, path: string): Folder | undefined => {
  if (!folders || path === '') return undefined;
  return [...folders]
    .sort((a, b) => b.path.length - a.path.length)
    .find((entry) => path === entry.path || path.startsWith(`${entry.path}/`));
};

const trimSlash = (value: string) => value.replace(/\/+$/, '');

const parentOf = (path: string) => path.split('/').slice(0, -1).join('/');

// Each segment is encoded but the separators are not, so a file named "Q1 report
// (final).csv" produces a working link.
const encodePath = (path: string) => path.split('/').map(encodeURIComponent).join('/');

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
};

// Intelligent tiering moves objects between access tiers on its own. The label is
// worth showing because a partner asking "why was that download slow" is usually
// looking at an object that has aged into a colder tier.
const storageLabel = (storageClass?: string) => {
  if (!storageClass || storageClass === 'STANDARD') return '';
  if (storageClass === 'INTELLIGENT_TIERING') return 'intelligent tiering';
  return storageClass.toLowerCase().replace(/_/g, ' ');
};
